/**
 * "Let a colleague see this."
 *
 * The granting affordance for owner-initiated sharing. Enforcement needs no UI —
 * a list the viewer may not see simply returns less — so this dialog is the
 * whole user-facing surface of the feature.
 *
 * Two things it is careful about:
 *
 * 1. **Saying what a level means, next to the level.** "Can run" and "Can edit"
 *    are not self-explanatory, and a wrong guess hands somebody more than was
 *    intended. The help text sits under the control rather than behind a
 *    tooltip.
 * 2. **Saying that a project carries its pipelines.** Sharing a project shares
 *    everything in it. That is the useful behaviour and the surprising one, so
 *    it is stated where the decision is made.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Radio,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { IconShare, IconTrash } from "@tabler/icons-react";

import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  PERMISSIONS,
  PERMISSION_HELP,
  PERMISSION_LABEL,
  type Permission,
  type RecipientOut,
  type ShareOut,
  createShare,
  listRecipients,
  listShares,
  revokeShare,
} from "@/shared/api/sharing";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";

export interface ShareDialogProps {
  opened: boolean;
  onClose: () => void;
  /** `project` or `pipeline`. */
  resourceType: "project" | "pipeline";
  resourceId: string;
  /** Shown in the title so the person can see what they are about to share. */
  resourceName: string;
  /** Only an owner may share; the caller decides and hides the button. */
  canShare: boolean;
}

export function ShareDialog({
  opened,
  onClose,
  resourceType,
  resourceId,
  resourceName,
  canShare,
}: ShareDialogProps) {
  const getToken = useAccessToken();
  const [recipients, setRecipients] = useState<RecipientOut[]>([]);
  const [shares, setShares] = useState<ShareOut[]>([]);
  const [subject, setSubject] = useState<string | null>(null);
  const [permission, setPermission] = useState<Permission>("read");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const [people, current] = await Promise.all([
        listRecipients(token),
        listShares(token, resourceType, resourceId),
      ]);
      setRecipients(people.items);
      // Revoked rows are kept server-side for the access history; this list is
      // "who can see it now", so the revoked ones are not shown.
      setShares(current.items.filter((s) => !s.revoked_at_utc));
    } catch (err) {
      toastApiError("Could not load sharing", err);
    } finally {
      setLoading(false);
    }
  }, [getToken, resourceType, resourceId]);

  useEffect(() => {
    if (opened) void refresh();
  }, [opened, refresh]);

  const nameOf = (share: ShareOut) => {
    const match = recipients.find(
      (r) => r.subject_type === share.subject_type && r.subject_id === share.subject_id,
    );
    return match?.display_name ?? share.subject_id;
  };

  const onShare = async () => {
    if (!subject) return;
    // `subject` is "<type>:<id>" — one Select value, because a picker where the
    // kind and the name are two controls invites choosing a person and leaving
    // the kind on "role".
    const separator = subject.indexOf(":");
    const subjectType = subject.slice(0, separator);
    const subjectId = subject.slice(separator + 1);
    setBusy(true);
    try {
      await createShare(await getToken(), {
        resource_type: resourceType,
        resource_id: resourceId,
        subject_type: subjectType,
        subject_id: subjectId,
        permission,
        reason: reason.trim() || "Shared from the app",
      });
      toastSuccess("Shared", `${resourceName} is now shared.`);
      setSubject(null);
      setReason("");
      await refresh();
    } catch (err) {
      toastApiError("Could not share", err);
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (share: ShareOut) => {
    setBusy(true);
    try {
      await revokeShare(await getToken(), share.grant_id, "Revoked from the app");
      toastSuccess("Access removed", `${nameOf(share)} can no longer see it.`);
      await refresh();
    } catch (err) {
      toastApiError("Could not remove access", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={`Share “${resourceName}”`} size="lg">
      <Stack gap="md">
        {resourceType === "project" && (
          <Alert color="blue" variant="light">
            Sharing a project shares <b>every pipeline inside it</b>, at this level. You can still
            give someone more on one pipeline by sharing that pipeline too.
          </Alert>
        )}

        {canShare ? (
          <Stack gap="sm">
            <Select
              label="Share with"
              placeholder={recipients.length ? "Pick a person or a role" : "Loading…"}
              searchable
              value={subject}
              data={[
                {
                  group: "People",
                  items: recipients
                    .filter((r) => r.subject_type === "user")
                    .map((r) => ({
                      value: `${r.subject_type}:${r.subject_id}`,
                      label: r.display_name,
                    })),
                },
                {
                  group: "Roles — access follows joiners and leavers",
                  items: recipients
                    .filter((r) => r.subject_type === "role")
                    .map((r) => ({
                      value: `${r.subject_type}:${r.subject_id}`,
                      label: r.display_name,
                    })),
                },
              ]}
              onChange={setSubject}
              disabled={busy}
            />

            <Radio.Group
              label="They can"
              value={permission}
              onChange={(v) => setPermission(v as Permission)}
            >
              <Stack gap="xs" mt="xs">
                {PERMISSIONS.map((p) => (
                  <Radio
                    key={p}
                    value={p}
                    disabled={busy}
                    label={PERMISSION_LABEL[p]}
                    description={PERMISSION_HELP[p]}
                  />
                ))}
              </Stack>
            </Radio.Group>

            <Text size="xs" c="dimmed">
              Sharing never lets someone share it on, or delete it. Those stay with you.
            </Text>

            <TextInput
              label="Why"
              description="Recorded with the share, so the access history says more than who and when."
              placeholder="Reviewing the migration with me"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              disabled={busy}
            />

            <Group justify="flex-end">
              <Button
                leftSection={<IconShare size={16} />}
                onClick={() => void onShare()}
                loading={busy}
                disabled={!subject}
              >
                Share
              </Button>
            </Group>
          </Stack>
        ) : (
          <Alert color="gray" variant="light">
            Only the owner can change who this is shared with.
          </Alert>
        )}

        <Stack gap="xs">
          <Text fw={600} size="sm">
            Shared with
          </Text>
          {loading ? (
            <Loader size="sm" />
          ) : shares.length === 0 ? (
            <Text size="sm" c="dimmed">
              Nobody yet — only you can see it.
            </Text>
          ) : (
            <Table highlightOnHover>
              <Table.Tbody>
                {shares.map((share) => (
                  <Table.Tr key={share.grant_id}>
                    <Table.Td>
                      {nameOf(share)}
                      {share.subject_type === "role" && (
                        <Badge ml="xs" size="xs" variant="light">
                          role
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light">
                        {PERMISSION_LABEL[share.permission as Permission] ?? share.permission}
                      </Badge>
                    </Table.Td>
                    <Table.Td width={40}>
                      {canShare && (
                        <Button
                          variant="subtle"
                          color="red"
                          size="compact-sm"
                          onClick={() => void onRevoke(share)}
                          disabled={busy}
                          aria-label={`Remove access for ${nameOf(share)}`}
                        >
                          <IconTrash size={16} />
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
}
